import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  BaseChapterProps,
  BaseProfileFieldsValues,
  ProfileFormValues,
  ProofOfActualResidenceFieldsValues,
} from '@/pages/UserAccount/Profile';
import { FormHasDirtyFields } from '@/pages/UserAccount/Profile/ui/ProfilePresentational';

import { IntegrationSystem, SelectOption } from '@/shared/types';

import { DocumentTypeEntity, AddressType } from '@/api/types';

import BaseChapterTemplate from './BaseChapterTemplate/BaseChapterTemplate';
import { UniversalDocumentFields } from './UniversalDocumentFields';

interface ProofOfActualResidenceProps extends BaseChapterProps {
  onSubmit: (values: ProfileFormValues) => void;
  actualAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  setFormHasDirtyFields: (value: FormHasDirtyFields) => void;
  activeKey: Array<NonNullable<FormHasDirtyFields>>;
}

const proofOfActualResidenceFields = [AddressType.LIVING, 'documents'] satisfies Array<
  keyof ProofOfActualResidenceFieldsValues | keyof BaseProfileFieldsValues
>;

export const ProofOfActualResidenceData: FC<ProofOfActualResidenceProps> = ({
  onSubmit,
  onReset,
  actualAddressDocumentTypeEntities,
  setFormHasDirtyFields,
  activeKey,
}) => {
  const {
    control,
    watch,
    handleSubmit,
    formState: { dirtyFields },
  } = useFormContext<ProfileFormValues>();

  const isProofOfActualResidenceFieldsDirty = Object.keys(dirtyFields).some((key) =>
    proofOfActualResidenceFields.includes(key as keyof Omit<ProofOfActualResidenceFieldsValues, 'actualDocTypeCode'>),
  );

  useEffect(() => {
    if (!isProofOfActualResidenceFieldsDirty) return setFormHasDirtyFields(null);

    setFormHasDirtyFields('proofActual');
  }, [isProofOfActualResidenceFieldsDirty]);

  useEffect(() => {
    if (!activeKey.includes('proofActual') && isProofOfActualResidenceFieldsDirty) onReset();
  }, [activeKey]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BaseChapterTemplate
        fields={[
          <UniversalDocumentFields
            fieldName={AddressType.LIVING}
            key="documentFields"
            documentTypeEntities={actualAddressDocumentTypeEntities as SelectOption[]}
            layout="profile"
            control={control}
            watch={watch}
          />,
        ]}
        isDisabledSubmitAction={isProofOfActualResidenceFieldsDirty}
        onReset={onReset}
      />
    </form>
  );
};
